import { useState } from "react";
import { Button, Group } from "@mantine/core";
import { BracketsCurlyIcon, FilePdfIcon } from "@phosphor-icons/react";
import { showNotification } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import { ReturnApi } from "../../apis";
import type { IReturnGetResponse } from "../../apis/return";

interface Props {
  returnId: string;
  detail: IReturnGetResponse;
}

// §5 audit export. JSON is built client-side from the detail's audit data;
// PDF is rendered server-side (media-service) and opened by URL.
export function ReturnAuditExport ({ returnId, detail }: Props){
  const { t } = useTranslation("returns");
  const [pdfBusy, setPdfBusy] = useState(false);

  const exportJson = () => {
    const rr = detail.returnRequest;
    const payload = {
      returnNumber: rr.returnNumber,
      status      : rr.status,
      requestType : rr.requestType,
      buyer       : { id: rr.buyerBusinessId, name: rr.buyerBusinessName },
      seller      : { id: rr.sellerBusinessId, name: rr.sellerBusinessName },
      requestedAt : rr.requestedAt,
      completedAt : rr.completedAt,
      timeline    : detail.timeline,
      pendingSteps: rr.pendingSteps,
      exportedAt  : new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `audit-${rr.returnNumber}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await ReturnApi.auditPdf(returnId);
      if (res?.url) window.open(res.url, "_blank", "noopener");
      else throw new Error(t("audit.pdfUrlEmpty"));
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Group gap="xs">
      <Button size="sm" variant="default" leftSection={<BracketsCurlyIcon size={16} />} onClick={exportJson}>
        {t("audit.downloadJson")}
      </Button>
      <Button size="sm" variant="default" leftSection={<FilePdfIcon size={16} />} onClick={exportPdf} loading={pdfBusy}>
        {t("audit.pdfReport")}
      </Button>
    </Group>
  );
}
